import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import Input from './Input';
import Icon from './Icon';
import type { ReactNode } from 'react';
type NumberInputChangeSource = 'INC' | 'DEC' | 'INPUT';
type NumberInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'min' | 'max'> & {
  id?: string;
  name?: string;
  value: number | undefined;
  /** @default 0 */
  min?: number | null;
  max?: number | null;
  /** @default 1 */
  step?: number;
  /** @default false */
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  suffix?: ReactNode;
  prefix?: ReactNode;
  allowUndefined?: boolean;
  /** @default false */
  integerOnly?: boolean;
  autoFocus?: boolean;
  /** @default false */
  hideButtons?: boolean;
  /** @default false */
  compact?: boolean;
  /** @default false */
  externalButtons?: boolean;
  externalButtonsContainerClassName?: string;
  onChange?: (value: number | undefined, source: NumberInputChangeSource) => void;
};
const ACCURACY = 1000;
export const increment = (val1: number, val2: number) => (val1 * ACCURACY + val2 * ACCURACY) / ACCURACY;
export const decrement = (val1: number, val2: number) => (val1 * ACCURACY - val2 * ACCURACY) / ACCURACY;
export default function NumberInput(t0) {
  const $ = _c(122);
  let autoFocus;
  let className;
  let externalButtonsContainerClassName;
  let id;
  let integerOnly;
  let max;
  let min;
  let name;
  let onBlur;
  let onChange;
  let prefix;
  let props;
  let suffix;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let value;
  if ($[0] !== t0) {
    const {
      id: t6,
      name: t7,
      value: t8,
      min: t9,
      max: t10,
      step: t11,
      disabled: t12,
      className: t13,
      suffix: t14,
      prefix: t15,
      allowUndefined,
      autoFocus: t16,
      onChange: t17,
      integerOnly: t18,
      externalButtons: t19,
      externalButtonsContainerClassName: t20,
      hideButtons: t21,
      compact: t22,
      onBlur: t23,
      ...t24
    } = t0;
    id = t6;
    name = t7;
    value = t8;
    min = t9;
    max = t10;
    t1 = t11;
    t2 = t12;
    className = t13;
    suffix = t14;
    prefix = t15;
    autoFocus = t16;
    onChange = t17;
    integerOnly = t18;
    t3 = t19;
    externalButtonsContainerClassName = t20;
    t4 = t21;
    t5 = t22;
    onBlur = t23;
    props = t24;
    $[0] = t0;
    $[1] = autoFocus;
    $[2] = className;
    $[3] = externalButtonsContainerClassName;
    $[4] = id;
    $[5] = integerOnly;
    $[6] = max;
    $[7] = min;
    $[8] = name;
    $[9] = onBlur;
    $[10] = onChange;
    $[11] = prefix;
    $[12] = props;
    $[13] = suffix;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = value;
  } else {
    autoFocus = $[1];
    className = $[2];
    externalButtonsContainerClassName = $[3];
    id = $[4];
    integerOnly = $[5];
    max = $[6];
    min = $[7];
    name = $[8];
    onBlur = $[9];
    onChange = $[10];
    prefix = $[11];
    props = $[12];
    suffix = $[13];
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    value = $[19];
  }
  const step = t1 === undefined ? 1 : t1;
  const disabled = t2 === undefined ? false : t2;
  const externalButtons = t3 === undefined ? false : t3;
  const hideButtons = t4 === undefined ? false : t4;
  const compact = t5 === undefined ? false : t5;
  let t6;
  if ($[20] !== min || $[21] !== value) {
    t6 = value === undefined || value === null || String(value).trim() === "" || typeof min === "number" && value === min;
    $[20] = min;
    $[21] = value;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const minButtonDisabled = t6;
  const maxButtonDisabled = typeof max === "number" && value === max;
  let t7;
  if ($[23] !== integerOnly || $[24] !== min || $[25] !== minButtonDisabled || $[26] !== onChange || $[27] !== step || $[28] !== value) {
    t7 = () => {
      if (minButtonDisabled) {
        return;
      }
      let newValue = decrement(value ?? min ?? 0, step);
      if (integerOnly) {
        newValue = Math.round(newValue);
      }
      onChange?.(newValue, "DEC");
    };
    $[23] = integerOnly;
    $[24] = min;
    $[25] = minButtonDisabled;
    $[26] = onChange;
    $[27] = step;
    $[28] = value;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const handleDecrement = t7;
  let t8;
  if ($[30] !== integerOnly || $[31] !== maxButtonDisabled || $[32] !== min || $[33] !== onChange || $[34] !== step || $[35] !== value) {
    t8 = () => {
      if (maxButtonDisabled) {
        return;
      }
      let newValue_0 = increment(value ?? min ?? 0, step);
      if (integerOnly) {
        newValue_0 = Math.round(newValue_0);
      }
      onChange?.(newValue_0, "INC");
    };
    $[30] = integerOnly;
    $[31] = maxButtonDisabled;
    $[32] = min;
    $[33] = onChange;
    $[34] = step;
    $[35] = value;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  const handleIncrement = t8;
  if (externalButtons) {
    let t9;
    if ($[37] !== externalButtonsContainerClassName) {
      t9 = cx("flex flex-1 items-center gap-2", externalButtonsContainerClassName);
      $[37] = externalButtonsContainerClassName;
      $[38] = t9;
    } else {
      t9 = $[38];
    }
    const t10 = minButtonDisabled && "pointer-events-none opacity-30";
    let t11;
    if ($[39] !== t10) {
      t11 = cx("flex size-14 shrink-0 items-center justify-center rounded-4xl border-0 bg-white text-turqoise ring-1 ring-gray-200 transition", "hover:ring-turqoise focus:ring-turqoise", t10);
      $[39] = t10;
      $[40] = t11;
    } else {
      t11 = $[40];
    }
    let t12;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Icon name="minus" />;
      $[41] = t12;
    } else {
      t12 = $[41];
    }
    let t13;
    if ($[42] !== handleDecrement || $[43] !== minButtonDisabled || $[44] !== t11) {
      t13 = <button type="button" onClick={handleDecrement} disabled={minButtonDisabled} className={t11}>{t12}</button>;
      $[42] = handleDecrement;
      $[43] = minButtonDisabled;
      $[44] = t11;
      $[45] = t13;
    } else {
      t13 = $[45];
    }
    const t14 = min ?? undefined;
    const t15 = max ?? undefined;
    const t16 = autoFocus || false;
    let t17;
    if ($[46] !== integerOnly) {
      t17 = event => {
        if (integerOnly && [".", ","].includes(event.key)) {
          event.preventDefault();
        }
      };
      $[46] = integerOnly;
      $[47] = t17;
    } else {
      t17 = $[47];
    }
    let t18;
    if ($[48] !== integerOnly || $[49] !== max || $[50] !== onChange) {
      t18 = event_0 => {
        const inputValue = event_0.target.value.trim();
        if (inputValue === "") {
          onChange?.(undefined, "INPUT");
          return;
        }
        let parsed = parseFloat(inputValue);
        if (!Number.isFinite(parsed) || parsed < 0) {
          onChange?.(undefined, "INPUT");
          return;
        }
        if (integerOnly) {
          parsed = Math.round(parsed);
        }
        if (typeof max === "number" && parsed > max) {
          parsed = max;
        }
        onChange?.(parsed, "INPUT");
      };
      $[48] = integerOnly;
      $[49] = max;
      $[50] = onChange;
      $[51] = t18;
    } else {
      t18 = $[51];
    }
    const t19 = disabled && "pointer-events-none";
    const t20 = suffix || prefix ? "w-12" : "w-full min-w-20";
    let t21;
    if ($[52] !== className || $[53] !== t19 || $[54] !== t20) {
      t21 = cx("[&>div>input]:text-center", t19, t20, "flex-1 px-6", className);
      $[52] = className;
      $[53] = t19;
      $[54] = t20;
      $[55] = t21;
    } else {
      t21 = $[55];
    }
    let t22;
    if ($[56] !== disabled || $[57] !== id || $[58] !== name || $[59] !== prefix || $[60] !== props || $[61] !== suffix || $[62] !== t14 || $[63] !== t15 || $[64] !== t16 || $[65] !== t17 || $[66] !== t18 || $[67] !== t21 || $[68] !== value) {
      t22 = <Input {...props} id={id} name={name} value={value} min={t14} max={t15} type="number" disabled={disabled} onWheel={_temp} autoFocus={t16} noPadding={true} prefix={prefix} suffix={suffix} onKeyDown={t17} onChange={t18} className={t21} />;
      $[56] = disabled;
      $[57] = id;
      $[58] = name;
      $[59] = prefix;
      $[60] = props;
      $[61] = suffix;
      $[62] = t14;
      $[63] = t15;
      $[64] = t16;
      $[65] = t17;
      $[66] = t18;
      $[67] = t21;
      $[68] = value;
      $[69] = t22;
    } else {
      t22 = $[69];
    }
    const t23 = maxButtonDisabled && "pointer-events-none opacity-30";
    let t24;
    if ($[70] !== t23) {
      t24 = cx("flex size-14 shrink-0 items-center justify-center rounded-4xl border-0 bg-white text-turqoise ring-1 ring-gray-200 transition", "hover:ring-turqoise focus:ring-turqoise", t23);
      $[70] = t23;
      $[71] = t24;
    } else {
      t24 = $[71];
    }
    let t25;
    if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <Icon name="plus" />;
      $[72] = t25;
    } else {
      t25 = $[72];
    }
    let t26;
    if ($[73] !== handleIncrement || $[74] !== maxButtonDisabled || $[75] !== t24) {
      t26 = <button type="button" onClick={handleIncrement} disabled={maxButtonDisabled} className={t24}>{t25}</button>;
      $[73] = handleIncrement;
      $[74] = maxButtonDisabled;
      $[75] = t24;
      $[76] = t26;
    } else {
      t26 = $[76];
    }
    let t27;
    if ($[77] !== onBlur || $[78] !== t13 || $[79] !== t22 || $[80] !== t26 || $[81] !== t9) {
      t27 = <div className={t9} onBlur={onBlur}>{t13}{t22}{t26}</div>;
      $[77] = onBlur;
      $[78] = t13;
      $[79] = t22;
      $[80] = t26;
      $[81] = t9;
      $[82] = t27;
    } else {
      t27 = $[82];
    }
    return t27;
  }
  const t9 = min ?? undefined;
  const t10 = max ?? undefined;
  const t11 = autoFocus || false;
  let t12;
  if ($[83] !== compact || $[84] !== handleDecrement || $[85] !== hideButtons || $[86] !== minButtonDisabled) {
    t12 = !hideButtons && <NumberInputButton onClick={handleDecrement} disabled={minButtonDisabled} className={cx(compact ? "ml-2 mr-0.5" : "ml-3 mr-1", minButtonDisabled && "pointer-events-none opacity-30")}><Icon name="minus" /></NumberInputButton>;
    $[83] = compact;
    $[84] = handleDecrement;
    $[85] = hideButtons;
    $[86] = minButtonDisabled;
    $[87] = t12;
  } else {
    t12 = $[87];
  }
  let t13;
  if ($[88] !== compact || $[89] !== handleIncrement || $[90] !== hideButtons || $[91] !== maxButtonDisabled) {
    t13 = !hideButtons && <NumberInputButton onClick={handleIncrement} className={cx(compact ? "ml-0.5 mr-2" : "ml-1 mr-3", maxButtonDisabled && "pointer-events-none opacity-30")}><Icon name="plus" /></NumberInputButton>;
    $[88] = compact;
    $[89] = handleIncrement;
    $[90] = hideButtons;
    $[91] = maxButtonDisabled;
    $[92] = t13;
  } else {
    t13 = $[92];
  }
  let t14;
  if ($[93] !== integerOnly) {
    t14 = event_1 => {
      if (integerOnly && [".", ","].includes(event_1.key)) {
        event_1.preventDefault();
      }
    };
    $[93] = integerOnly;
    $[94] = t14;
  } else {
    t14 = $[94];
  }
  let t15;
  if ($[95] !== integerOnly || $[96] !== max || $[97] !== onChange) {
    t15 = event_2 => {
      const inputValue_0 = event_2.target.value.trim();
      if (inputValue_0 === "") {
        onChange?.(undefined, "INPUT");
        return;
      }
      let parsed_0 = parseFloat(inputValue_0);
      if (!Number.isFinite(parsed_0) || parsed_0 < 0) {
        onChange?.(undefined, "INPUT");
        return;
      }
      if (integerOnly) {
        parsed_0 = Math.round(parsed_0);
      }
      if (typeof max === "number" && parsed_0 > max) {
        parsed_0 = max;
      }
      onChange?.(parsed_0, "INPUT");
    };
    $[95] = integerOnly;
    $[96] = max;
    $[97] = onChange;
    $[98] = t15;
  } else {
    t15 = $[98];
  }
  const t16 = compact && "[&>div>input]:py-2 [&>div>input]:text-sm";
  const t17 = disabled && "pointer-events-none";
  const t18 = suffix || prefix ? "w-9" : "w-full";
  const t19 = hideButtons ? "px-6" : "";
  let t20;
  if ($[99] !== className || $[100] !== t16 || $[101] !== t17 || $[102] !== t18 || $[103] !== t19) {
    t20 = cx("[&>div>input]:text-center", t16, t17, t18, t19, className);
    $[99] = className;
    $[100] = t16;
    $[101] = t17;
    $[102] = t18;
    $[103] = t19;
    $[104] = t20;
  } else {
    t20 = $[104];
  }
  let t21;
  if ($[105] !== disabled || $[106] !== id || $[107] !== name || $[108] !== onBlur || $[109] !== prefix || $[110] !== props || $[111] !== suffix || $[112] !== t10 || $[113] !== t11 || $[114] !== t12 || $[115] !== t13 || $[116] !== t14 || $[117] !== t15 || $[118] !== t20 || $[119] !== t9 || $[120] !== value) {
    t21 = <Input {...props} id={id} name={name} value={value} min={t9} max={t10} type="number" disabled={disabled} onWheel={_temp2} autoFocus={t11} noPadding={true} prefix={prefix} suffix={suffix} onBlur={onBlur} actionBefore={t12} actionAfter={t13} onKeyDown={t14} onChange={t15} className={t20} />;
    $[105] = disabled;
    $[106] = id;
    $[107] = name;
    $[108] = onBlur;
    $[109] = prefix;
    $[110] = props;
    $[111] = suffix;
    $[112] = t10;
    $[113] = t11;
    $[114] = t12;
    $[115] = t13;
    $[116] = t14;
    $[117] = t15;
    $[118] = t20;
    $[119] = t9;
    $[120] = value;
    $[121] = t21;
  } else {
    t21 = $[121];
  }
  return t21;
}
function _temp2() {
  return (document.activeElement as HTMLElement).blur();
}
function _temp() {
  return (document.activeElement as HTMLElement).blur();
}
export const NumberInputButton: React.FC<{
  className?: string;
  disabled?: boolean;
  children: React.ReactNode;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}> = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("text-turqoise", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <button {...props} type="button" className={t1} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};